import React, { useState, useEffect, useCallback } from 'react';
import useTabFromUrl from '../../hooks/useTabFromUrl';
import { Inbox, Upload, AlertCircle, Circle } from 'lucide-react';
import { AssignmentDB } from '../../utils/assignmentsData';
import { mockUser } from '../../utils/mockData';
import MyAssignmentsView from './MyAssignmentsView';
import { Button } from '../ui';
import { getStatusColor, getPriorityColor } from '../../utils/colorMap';
import { formatDate, formatDateTime } from '../../utils/formatters';

export default function AssignmentsView({ userId = null, userRole = 'manager' }) {
  const currentUserId = userId || mockUser.id;
  const [activeTab, setActiveTab] = useTabFromUrl('assignedToMe');
  const [assignmentsMade, setAssignmentsMade] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadAssignmentsMade = useCallback(() => {
    let list = AssignmentDB.getAssignmentsByUser(currentUserId, filterStatus);

    if (filterPriority !== 'all') {
      list = list.filter(a => a.priority === filterPriority);
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(a =>
        (a.itemTitle && a.itemTitle.toLowerCase().includes(q)) ||
        (a.justification && a.justification.toLowerCase().includes(q)) ||
        (a.assignedToName && a.assignedToName.toLowerCase().includes(q))
      );
    }

    setAssignmentsMade(list);
  }, [currentUserId, filterStatus, filterPriority, searchTerm]);

  useEffect(() => {
    if (activeTab === 'assignedByMe') {
      loadAssignmentsMade();
    }
  }, [activeTab, loadAssignmentsMade]);

  const tabs = [
    { id: 'assignedToMe', label: 'Assigned to Me', Icon: Inbox },
    { id: 'assignedByMe', label: 'Assigned by Me', Icon: Upload }
  ];

  const activeCount = assignmentsMade.filter(a => a.status === 'active').length;
  const completedCount = assignmentsMade.filter(a => a.status === 'completed').length;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 className="dashboard-title">Assignments</h1>
          <p className="dashboard-subtitle">Manage tasks assigned to you and assignments you've made</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <nav className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto bg-white rounded-t-lg px-2 pt-2">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant="ghost"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 border-b-2 whitespace-nowrap transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.Icon && <tab.Icon size={18} strokeWidth={2} />}
            <span>{tab.label}</span>
          </Button>
        ))}
      </nav>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'assignedToMe' ? (
          <MyAssignmentsView userId={currentUserId} viewType="assignedToMe" />
        ) : (
          <div>
            <div className="grid grid-cols-3 gap-5" style={{ marginBottom: 24 }}>
              <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-aqua">
                <div className="p-5">
                  <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Active Assignments</div>
                  <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{activeCount}</div>
                  <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">{assignmentsMade.length} total</div>
                </div>
              </div>
              <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-green-nn">
                <div className="p-5">
                  <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Completed</div>
                  <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">{completedCount}</div>
                  <div className="flex items-center gap-1 text-[13px] mb-4 text-green-nn">This period</div>
                </div>
              </div>
              <div className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative border-l-4 border-l-yellow-nn">
                <div className="p-5">
                  <div className="text-[13px] text-text-dark-secondary font-normal mb-2 block">Team Members</div>
                  <div className="text-[34px] font-bold text-text-dark tracking-tight mb-1.5 block">
                    {new Set(assignmentsMade.map(a => a.assignedTo)).size}
                  </div>
                  <div className="flex items-center gap-1 text-[13px] mb-4 text-text-dark-secondary">With assignments</div>
                </div>
              </div>
            </div>

            <div className="filter-panel bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative" style={{ marginBottom: 24, padding: 20 }}>
              <div className="filter-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div>
                  <label className="text-body font-medium" style={{ display: 'block', marginBottom: 5 }}>Status</label>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua">
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
                <div>
                  <label className="text-body font-medium" style={{ display: 'block', marginBottom: 5 }}>Priority</label>
                  <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua">
                    <option value="all">All Priorities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-body font-medium" style={{ display: 'block', marginBottom: 5 }}>Search</label>
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search assignments..." className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua" />
                </div>
              </div>
            </div>

            {assignmentsMade.length === 0 ? (
              <div className="text-center py-16 px-5 text-text-dark-secondary bg-nn-white rounded-[14px] shadow-card overflow-hidden" style={{ border: '1px solid #E5E7EB' }}>
                <p className="text-text-muted" style={{ marginBottom: 20 }}>
                  {filterStatus !== 'all' || filterPriority !== 'all' || searchTerm
                    ? 'No assignments match your filters.'
                    : "You haven't assigned any tasks yet."}
                </p>
              </div>
            ) : (
              <div className="initiatives-list" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {assignmentsMade.map(assignment => {
                  const isOverdue = assignment.status === 'active' && assignment.dueDate && new Date(assignment.dueDate) < new Date();
                  const assigneeWorkload = AssignmentDB.getWorkloadForUser(assignment.assignedTo);
                  return (
                    <div
                      key={assignment.id}
                      className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative"
                      style={{
                        borderLeft: `4px solid ${getStatusColor(assignment.status)}`,
                        background: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div className="p-5">
                        <div className="initiative-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 15 }}>
                          <div style={{ flex: 1 }}>
                            <h3 className="text-lg font-medium" style={{ margin: '0 0 5px 0' }}>{assignment.itemTitle}</h3>
                            <div className="text-body text-text-muted">
                              Assigned to: <strong>{assignment.assignedToName}</strong>
                              {assignment.dueDate && ` | Due: ${formatDate(assignment.dueDate)}`}
                            </div>
                            {assigneeWorkload && (
                              <div className="text-body-sm text-text-muted" style={{ marginTop: 4 }}>
                                Assignee workload: {assigneeWorkload.count} assignments{' '}
                                {assigneeWorkload.level === 'critical' ? <AlertCircle size={14} color="#D94F4F" strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : assigneeWorkload.level === 'red' ? <Circle size={10} fill="#D94F4F" color="#D94F4F" strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : assigneeWorkload.level === 'yellow' ? <Circle size={10} fill="#E5A820" color="#E5A820" strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : <Circle size={10} fill="#2EB87A" color="#2EB87A" strokeWidth={2} style={{ verticalAlign: 'middle' }} />}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{
                              background: getPriorityColor(assignment.priority) + '20',
                              color: getPriorityColor(assignment.priority),
                              padding: '4px 12px',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600
                            }}>
                              {assignment.priority.toUpperCase()}
                            </span>
                            <span style={{
                              background: getStatusColor(assignment.status) + '20',
                              color: getStatusColor(assignment.status),
                              padding: '4px 12px',
                              borderRadius: 12,
                              fontSize: 12,
                              fontWeight: 600
                            }}>
                              {assignment.status.toUpperCase()}
                            </span>
                            {isOverdue && (
                              <span style={{
                                background: '#EF444420',
                                color: '#EF4444',
                                padding: '4px 12px',
                                borderRadius: 12,
                                fontSize: 12,
                                fontWeight: 600
                              }}>
                                OVERDUE
                              </span>
                            )}
                          </div>
                        </div>

                        {assignment.justification && (
                          <div style={{ marginBottom: 15, padding: 12, background: '#F9FAFB', borderRadius: 6 }}>
                            <div className="text-body-sm font-semibold text-text-dark" style={{ marginBottom: 4 }}>Reason</div>
                            <div className="text-body text-text-muted">{assignment.justification}</div>
                          </div>
                        )}

                        <div className="text-body-sm text-text-muted" style={{ display: 'flex', gap: 8, marginBottom: 15 }}>
                          <span>Item Type: <strong>{assignment.itemType}</strong></span>
                          {assignment.estimatedTime && <span>| Est. Time: <strong>{assignment.estimatedTime} min</strong></span>}
                          {assignment.createdAt && <span>| Assigned: <strong>{formatDateTime(assignment.createdAt)}</strong></span>}
                          {assignment.completedAt && <span>| Completed: <strong>{formatDateTime(assignment.completedAt)}</strong></span>}
                        </div>

                        {assignment.declineReason && (
                          <div style={{ marginBottom: 15, padding: 12, background: '#FEE2E2', borderRadius: 6, borderLeft: '3px solid #EF4444' }}>
                            <div className="text-body-sm font-semibold text-red-critical" style={{ marginBottom: 4 }}>Decline Reason</div>
                            <div className="text-body text-red-critical">{assignment.declineReason}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
